import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Dropzone from 'react-dropzone';
import moment from 'moment';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import VisibilityIcon from '@material-ui/icons/Visibility';
import DeleteIcon from '@material-ui/icons/Delete';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import { tenantActions } from '../../../redux/tenant.actions';
import BuyCertificateForm from './BuyCertificateForm';

const baseDropzoneStyle = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '15px',
  borderWidth: 2,
  borderRadius: 10,
  borderColor: '#C0C0C0',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#757575',
  outline: 'none',
  minHeight: 100,
  maxHeight: 100,
  cursor: 'pointer',
  textAlign: 'center',
  transition: 'border .24s ease-in-out',
};

const InventoryCheckIn = ({ flowId, openFlow }) => {
  const dispatch = useDispatch();

  const currentFlowId = flowId || openFlow?.openflow_id || openFlow?.id;

  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [inventoryDocuments, setInventoryDocuments] = useState([]);
  const [showBuyCertificateModal, setShowBuyCertificateModal] = useState(false);
  const [inventoryCertificateRequests, setInventoryCertificateRequests] = useState([]);
  const [uploading, setUploading] = useState(false);

  const certificateRequest = useSelector((state) => state?.tenant?.certificateRequest);

  useEffect(() => {
    if (currentFlowId) {
      dispatch(tenantActions.buyCer(currentFlowId));
    }
  }, [currentFlowId, dispatch]);

  useEffect(() => {
    if (openFlow?.open_flow_move_in && Object.keys(openFlow.open_flow_move_in).length > 0) {
      if (openFlow.open_flow_move_in.inventory_doc) {
        const docs = openFlow.open_flow_move_in.inventory_doc.split(',').filter(Boolean);
        setInventoryDocuments(docs);
      } else {
        setInventoryDocuments([]);
      }
    }
  }, [openFlow?.open_flow_move_in?.inventory_doc, openFlow]);

  useEffect(() => {
    if (certificateRequest && Array.isArray(certificateRequest)) {
      const filtered = certificateRequest.filter(
        (certificate) =>
          certificate.certificate_type === 'inventory' || certificate.certificate_type === 'checkin'
      );
      setInventoryCertificateRequests(filtered);
    }
  }, [certificateRequest]);

  const handleDelete = (doc) => {
    setSelectedDocument(doc);
    setShowDeleteModal(true);
  };

  const deleteDocument = () => {
    const documents = inventoryDocuments.filter((doc) => doc !== selectedDocument);
    setInventoryDocuments(documents);
    dispatch(
      tenantActions.uploadInventoryDocuments({
        flow_id: currentFlowId,
        inventory_documents: documents.join(','),
        isdelete: true,
        deleted_document: selectedDocument,
      })
    );
    setSelectedDocument(null);
    setShowDeleteModal(false);
  };

  const onDropCertificate = (acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length) {
      setUploading(true);
      const newImages = acceptedFiles.map((file) =>
        Object.assign(file, { preview: URL.createObjectURL(file) })
      );

      dispatch(
        tenantActions.uploadFiles(newImages, 'inventory_documents', (uploadResponse) => {
          setUploading(false);
          if (uploadResponse?.data && uploadResponse.data.length) {
            const currentDocs = [...inventoryDocuments];
            uploadResponse.data.forEach((item) => {
              const docName = typeof item === 'string' ? item : item?.openbrixName || item?.name || '';
              if (docName) currentDocs.push(docName);
            });
            setInventoryDocuments(currentDocs);
            dispatch(
              tenantActions.uploadInventoryDocuments({
                flow_id: currentFlowId,
                inventory_documents: currentDocs.join(','),
                deleted_document: null,
              })
            );
          }
        })
      );
    }
  };

  const handleBuy = () => {
    setShowBuyCertificateModal(true);
  };

  const handleBuyInventory = (payload) => {
    dispatch(
      tenantActions.buyCertificate(
        {
          ...payload,
          certificate_type: 'inventory',
          flow_id: currentFlowId,
        },
        () => {
          dispatch(tenantActions.buyCer(currentFlowId));
          setShowBuyCertificateModal(false);
        }
      )
    );
  };

  const isRenewed = openFlow?.is_renewed;

  const dropzoneStyle = useMemo(
    () => ({
      ...baseDropzoneStyle,
    }),
    []
  );

  return (
    <Card sx={{ borderRadius: '12px', border: '1px solid #edf2f7', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', my: 3, width: '100%' }}>
      <CardHeader
        title={
          <Typography variant="subtitle1" sx={{ color: '#0f294a', fontWeight: 700, fontSize: '18px' }}>
            {isRenewed ? 'Property Visit' : 'Inventory & Check in'}
          </Typography>
        }
        subheader={
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="body2" sx={{ color: '#5a6e85', fontSize: '14px', lineHeight: 1.5 }}>
              A National Inventory and Property Inspection service has integrated with tlyfePRO to simplify the process by pre-populating all required details. You can either purchase this service here on a tenancy by tenancy basis or upload your own version.
            </Typography>
          </Box>
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ pt: 1 }}>
        <Grid container spacing={3}>
          {/* Left Column: Buy Inventory & Check in */}
          <Grid item xs={12} sm={6}>
            <Card variant="outlined" sx={{ borderRadius: '10px', height: '100%', borderColor: '#e2e8f0', p: 2 }}>
              <CardHeader
                title={
                  <Typography variant="subtitle1" sx={{ color: '#0f294a', fontWeight: 700, fontSize: '15px' }}>
                    {isRenewed ? 'Buy Inspection' : 'Buy Inventory & Check in'}
                  </Typography>
                }
                subheader={
                  <Box sx={{ mt: 1.5 }}>
                    <Alert
                      icon={<InfoOutlinedIcon fontSize="small" sx={{ color: '#0288D1' }} />}
                      severity="info"
                      sx={{
                        backgroundColor: '#EBF5FF',
                        color: '#0288D1',
                        borderRadius: '8px',
                        fontSize: '13px',
                        lineHeight: 1.45,
                        '& .MuiAlert-message': { color: '#0288D1' },
                      }}
                    >
                      {isRenewed
                        ? 'Conducting property inspections from time to time helps you to ensure the property is being cared for by the tenants and properly maintained by the agent/landlord. NB If the property is subject to a mandatory, additional or selective licence, you will be required to undertake property visits and maintain a record of the visits, as a licence condition. Please check the licence to see how often they are required'
                        : 'You will need to have an inventory as evidence in the event that your client wishes to make a claim against the deposit for damage.'}
                    </Alert>
                  </Box>
                }
                sx={{ p: 0, mb: 2 }}
              />
              <CardContent sx={{ p: 0 }}>
                {inventoryCertificateRequests.length === 0 ? (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleBuy}
                      sx={{
                        backgroundColor: '#0288D1',
                        '&:hover': { backgroundColor: '#0277BD' },
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '14px',
                        py: 1,
                        px: 2.5,
                        borderRadius: '6px',
                      }}
                    >
                      {isRenewed ? 'Arrange Property visit' : 'Arrange Inventory checkin'}
                    </Button>
                  </Box>
                ) : (
                  inventoryCertificateRequests.map((item, index) => (
                    <Card
                      key={index}
                      variant="outlined"
                      sx={{
                        borderRadius: '8px',
                        p: 2,
                        mt: 2,
                        borderColor: '#edf2f7',
                        boxShadow: 'none',
                      }}
                    >
                      <Typography sx={{ color: '#475569', fontSize: '14px', mb: 1 }}>
                        Purchased On: {item?.createdAt ? moment(item.createdAt).format('DD/MM/YYYY') : 'N/A'}
                      </Typography>
                      <Typography sx={{ color: '#475569', fontSize: '14px', mb: 2 }}>
                        Appointment Date: {item?.ammendDate ? moment(item.ammendDate).format('DD/MM/YYYY') : 'N/A'}
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          if (item.management_link) {
                            window.open(item.management_link, '_blank');
                          }
                        }}
                        sx={{
                          color: '#0f294a',
                          borderColor: '#cbd5e1',
                          textTransform: 'uppercase',
                          fontWeight: 600,
                          fontSize: '12px',
                          letterSpacing: '0.5px',
                          px: 2,
                          py: 0.5,
                          borderRadius: '4px',
                          '&:hover': {
                            borderColor: '#0f294a',
                            backgroundColor: '#f8fafc',
                          },
                        }}
                      >
                        MANAGEMENT
                      </Button>
                    </Card>
                  ))
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column: Upload Inventory Documents */}
          <Grid item xs={12} sm={6}>
            <Card variant="outlined" sx={{ borderRadius: '10px', height: '100%', borderColor: '#e2e8f0', p: 2 }}>
              <CardHeader
                title={
                  <Typography variant="subtitle1" sx={{ color: '#0f294a', fontWeight: 700, fontSize: '15px' }}>
                    {isRenewed ? 'Property Visit Documentation' : 'Upload Inventory Documents'}
                  </Typography>
                }
                subheader={
                  <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px', mt: 0.5 }}>
                    {isRenewed
                      ? 'Store your property visit and inspection documents here.'
                      : 'Store your inventory documents here.'}
                  </Typography>
                }
                sx={{ p: 0, mb: 2 }}
              />
              <CardContent sx={{ p: 0 }}>
                <Dropzone
                  accept="image/*,application/pdf,.doc,.docx,.odt,.xls,.xlsx,.txt"
                  disabled={uploading}
                  onDrop={onDropCertificate}
                >
                  {({ getRootProps, getInputProps, isDragActive }) => (
                    <div
                      {...getRootProps()}
                      style={{
                        ...dropzoneStyle,
                        backgroundColor: isDragActive ? '#f0f9ff' : '#fafafa',
                        borderColor: isDragActive ? '#0288D1' : '#C0C0C0',
                      }}
                    >
                      <input {...getInputProps()} />
                      {uploading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CircularProgress size={20} />
                          <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                            Uploading document...
                          </Typography>
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: '13px', color: '#757575' }}>
                          "Drag 'n' drop or click here to select a document"
                        </Typography>
                      )}
                    </div>
                  )}
                </Dropzone>

                {inventoryDocuments && inventoryDocuments.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {inventoryDocuments.map((item, index) => (
                      <Card
                        key={index}
                        variant="outlined"
                        sx={{
                          borderRadius: '8px',
                          p: 1.5,
                          mt: 1.5,
                          borderColor: '#edf2f7',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          boxShadow: 'none',
                        }}
                      >
                        <Box sx={{ maxWidth: '75%', overflow: 'hidden' }}>
                          <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600, fontSize: '13px' }}>
                            Inventory Document {inventoryDocuments.length > 1 ? index + 1 : ''}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#64748b',
                              fontSize: '11px',
                              display: 'block',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {typeof item === 'string' ? item.split('/').pop() : 'Document'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title="View Document">
                            <IconButton
                              size="small"
                              sx={{ color: '#0288D1' }}
                              onClick={() => {
                                const targetUrl = item?.startsWith('http')
                                  ? item
                                  : `https://assets.openbrix.co.uk/uploads/${item}`;
                                window.open(targetUrl, '_blank');
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Document">
                            <IconButton
                              size="small"
                              sx={{ color: '#ef4444' }}
                              onClick={() => handleDelete(item)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Card>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteModal} onClose={() => setShowDeleteModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#0f294a' }}>Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#475569' }}>
            Are you sure, You want to delete this document.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setShowDeleteModal(false)}
            variant="contained"
            sx={{
              backgroundColor: '#FFFFFF',
              color: '#0f294a',
              border: '1px solid #cbd5e1',
              boxShadow: 'none',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#f8fafc', boxShadow: 'none' },
            }}
          >
            No
          </Button>
          <Button
            onClick={deleteDocument}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none', boxShadow: 'none' }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Buy Certificate Dialog */}
      <Dialog
        open={showBuyCertificateModal}
        onClose={() => setShowBuyCertificateModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#0f294a', pb: 1 }}>
          <b>{isRenewed ? 'Property Visit' : 'Inventory & Check in'}</b>
        </DialogTitle>
        <DialogContent dividers>
          <BuyCertificateForm
            onSubmit={handleBuyInventory}
            openFlow={openFlow}
            certificateType="inventory"
            onCancel={() => setShowBuyCertificateModal(false)}
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default InventoryCheckIn;
